import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import styles from './ChangePasswordPage.module.css';

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { updatePasswordAndClearFlag, signOut, mustChangePassword } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSaving(true);
    const { error: updateError } = await updatePasswordAndClearFlag(newPassword);
    setIsSaving(false);

    if (updateError) {
      setError(updateError.message || 'Unable to update password. Please try again.');
      return;
    }

    setSuccess(true);
    setTimeout(() => navigate('/'), 1200);
  };

  const handleCancel = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <img src="/peapil-logo.png" alt="Peapil Health" className={styles.logo} />
          <h1 className={styles.title}>
            {mustChangePassword ? 'Set a New Password' : 'Change Password'}
          </h1>
          {mustChangePassword && (
            <p className={styles.subtitle}>
              For your security, please choose a new password to continue.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && (
            <div className={styles.error}>
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className={styles.success}>
              <CheckCircle2 size={20} />
              <span>Password updated. Redirecting...</span>
            </div>
          )}

          <div className={styles.inputGroup}>
            <label htmlFor="newPassword" className={styles.label}>New Password</label>
            <div className={styles.inputWrapper}>
              <Lock size={20} className={styles.inputIcon} />
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                className={styles.input}
                autoComplete="new-password"
                disabled={isSaving || success}
              />
            </div>
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="confirmPassword" className={styles.label}>Confirm Password</label>
            <div className={styles.inputWrapper}>
              <Lock size={20} className={styles.inputIcon} />
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                className={styles.input}
                autoComplete="new-password"
                disabled={isSaving || success}
              />
            </div>
          </div>

          <button type="submit" className={styles.button} disabled={isSaving || success}>
            {isSaving ? 'Updating...' : 'Update Password'}
          </button>

          <button
            type="button"
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={isSaving || success}
          >
            Sign Out
          </button>
        </form>
      </div>
    </div>
  );
}
